// src/components/Dashboard.js
import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { baseURL } from "../utils/api";
import axios from "axios";
import "../css/Dashboard.css";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, hasPermission } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${baseURL}api/dashboard-summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(res.data);
      } catch (err) {
        console.error("Dashboard load error:", err);
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const hasWZAccess = hasPermission("docs_list") || hasPermission("docs_create") || hasPermission("docs_stats");
  const hasVehiclesAccess =
    hasPermission("vehicles_dashboard") ||
    hasPermission("vehicles_list") ||
    hasPermission("vehicles_faults") ||
    hasPermission("vehicles_services") ||
    hasPermission("vehicles_repairs") ||
    hasPermission("vehicles_workshop") ||
    hasPermission("vehicles_statistics") ||
    hasPermission("vehicles_settings");
  const hasAdminAccess = hasPermission("admin_panel");

  const formatCurrency = (val) => {
    if (!val) return "0 zł";
    return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN", maximumFractionDigits: 0 }).format(val);
  };

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("pl-PL");
  };

  const faultStatusLabel = { OPEN: "Zgłoszone", IN_PROGRESS: "W trakcie", RESOLVED: "Gotowe" };
  const faultStatusCls = { OPEN: "dash-tag--danger", IN_PROGRESS: "dash-tag--warning", RESOLVED: "dash-tag--success" };

  return (
    <div className="dashboard-page">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <h1>
            {/* <span className="dashboard-wave">👋</span> */}
            Witaj, {user?.forname && user?.name ? `${user.forname} ${user.name}` : user?.name || "Użytkowniku"}!
          </h1>
          <p className="dashboard-subtitle">
            {new Date().toLocaleDateString("pl-PL", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </p>
        </div>
      </div>

      {/* Stat cards */}
      {data && (
        <div className="dashboard-stats">
          {hasWZAccess && (
            <div className="dash-stat" onClick={() => navigate("/dokumenty")}>
              <div className="dash-stat-icon dash-stat-icon--blue"><i className="pi pi-file-edit" /></div>
              <div className="dash-stat-info">
                <span className="dash-stat-value">{data.wz.docsThisMonth}</span>
                <span className="dash-stat-label">Dokumentów w tym miesiącu</span>
              </div>
            </div>
          )}
          {hasWZAccess && user?.role === "Administrator" && (
            <div className="dash-stat" onClick={() => navigate("/dokumenty")}>
              <div className="dash-stat-icon dash-stat-icon--emerald"><i className="pi pi-wallet" /></div>
              <div className="dash-stat-info">
                <span className="dash-stat-value">{formatCurrency(data.wz.revenueNettoThisMonth)}</span>
                <span className="dash-stat-label">Wartość netto (miesiąc)</span>
              </div>
            </div>
          )}
          {hasVehiclesAccess && (
            <div className="dash-stat" onClick={() => navigate("/pojazdy")}>
              <div className="dash-stat-icon dash-stat-icon--green"><i className="pi pi-car" /></div>
              <div className="dash-stat-info">
                <span className="dash-stat-value">{data.vehicles.activeVehicles}</span>
                <span className="dash-stat-label">Aktywnych pojazdów</span>
              </div>
            </div>
          )}
          {hasVehiclesAccess && (
            <div className="dash-stat" onClick={() => navigate("/warsztat")}>
              <div className="dash-stat-icon dash-stat-icon--orange"><i className="pi pi-exclamation-triangle" /></div>
              <div className="dash-stat-info">
                <span className="dash-stat-value">{data.vehicles.openFaults + data.vehicles.inProgressFaults}</span>
                <span className="dash-stat-label">Otwartych usterek</span>
              </div>
            </div>
          )}
          {hasVehiclesAccess && (data.services.overdueVehicles > 0 || data.services.warningVehicles > 0) && (
            <div className="dash-stat dash-stat--alert" onClick={() => navigate("/pojazdy")}>
              <div className="dash-stat-icon dash-stat-icon--red"><i className="pi pi-clock" /></div>
              <div className="dash-stat-info">
                <span className="dash-stat-value">{data.services.overdueVehicles + data.services.warningVehicles}</span>
                <span className="dash-stat-label">Serwisy wymagające uwagi</span>
              </div>
            </div>
          )}
          {hasVehiclesAccess && hasPermission("vehicles_repairs") && user?.role === "Administrator" && (
            <div className="dash-stat" onClick={() => navigate("/naprawy")}>
              <div className="dash-stat-icon dash-stat-icon--purple"><i className="pi pi-wrench" /></div>
              <div className="dash-stat-info">
                <span className="dash-stat-value">{formatCurrency(data.repairs.repairCostThisMonth)}</span>
                <span className="dash-stat-label">Koszt napraw (miesiąc)</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Module tiles */}
      {hasWZAccess && (
        <>
          <h2 className="dashboard-section-title">Dokumenty WZ</h2>
          <div className="dashboard-modules">
            {hasPermission("docs_list") && (
              <div className="dash-module" style={{ "--mod-color": "#3b82f6", "--mod-bg": "#eff6ff" }}
                onClick={() => navigate("/dokumenty")}>
                <div className="dash-module-icon"><i className="pi pi-list" /></div>
                <div className="dash-module-body">
                  <h3>Lista dokumentów</h3>
                  <p>Przeglądanie dokumentów WZ / RW</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("docs_create") && (
              <div className="dash-module" style={{ "--mod-color": "#059669", "--mod-bg": "#ecfdf5" }}
                onClick={() => navigate("/dokumenty/nowy")}>
                <div className="dash-module-icon"><i className="pi pi-plus" /></div>
                <div className="dash-module-body">
                  <h3>Nowy dokument</h3>
                  <p>Utwórz nowy dokument wydania</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("docs_stats") && (
              <div className="dash-module" style={{ "--mod-color": "#6366f1", "--mod-bg": "#eef2ff" }}
                onClick={() => navigate("/dokumenty/statystyki")}>
                <div className="dash-module-icon"><i className="pi pi-chart-bar" /></div>
                <div className="dash-module-body">
                  <h3>Statystyki</h3>
                  <p>Zestawienia i analizy dokumentów</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
          </div>
        </>
      )}
      {hasVehiclesAccess && (
        <>
          <h2 className="dashboard-section-title">Pojazdy</h2>
          <div className="dashboard-modules">
            {hasPermission("vehicles_dashboard") && (
              <div className="dash-module" style={{ "--mod-color": "#10b981", "--mod-bg": "#ecfdf5" }}
                onClick={() => navigate("/pojazdy")}>
                <div className="dash-module-icon"><i className="pi pi-th-large" /></div>
                <div className="dash-module-body">
                  <h3>Dashboard</h3>
                  <p>Przegląd floty pojazdów</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_list") && (
              <div className="dash-module" style={{ "--mod-color": "#3b82f6", "--mod-bg": "#eff6ff" }}
                onClick={() => navigate("/pojazdy/lista")}>
                <div className="dash-module-icon"><i className="pi pi-list" /></div>
                <div className="dash-module-body">
                  <h3>Lista pojazdów</h3>
                  <p>Zarządzanie pojazdami</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_services") && (
              <div className="dash-module" style={{ "--mod-color": "#f59e0b", "--mod-bg": "#fffbeb" }}
                onClick={() => navigate("/serwisy")}>
                <div className="dash-module-icon"><i className="pi pi-shield" /></div>
                <div className="dash-module-body">
                  <h3>Serwisy</h3>
                  <p>Rejestr serwisów pojazdów</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_repairs") && (
              <div className="dash-module" style={{ "--mod-color": "#ef4444", "--mod-bg": "#fef2f2" }}
                onClick={() => navigate("/naprawy")}>
                <div className="dash-module-icon"><i className="pi pi-wrench" /></div>
                <div className="dash-module-body">
                  <h3>Naprawy</h3>
                  <p>Rejestr napraw pojazdów</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_faults") && (
              <div className="dash-module" style={{ "--mod-color": "#d97706", "--mod-bg": "#fff7ed" }}
                onClick={() => navigate("/zgloszenia")}>
                <div className="dash-module-icon"><i className="pi pi-exclamation-triangle" /></div>
                <div className="dash-module-body">
                  <h3>Zgłoś usterkę</h3>
                  <p>Zgłaszanie usterek pojazdów</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_workshop") && (
              <div className="dash-module" style={{ "--mod-color": "#8b5cf6", "--mod-bg": "#f5f3ff" }}
                onClick={() => navigate("/warsztat")}>
                <div className="dash-module-icon"><i className="pi pi-wrench" /></div>
                <div className="dash-module-body">
                  <h3>Warsztat</h3>
                  <p>Panel zarządzania warsztatem</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_statistics") && (
              <div className="dash-module" style={{ "--mod-color": "#6366f1", "--mod-bg": "#eef2ff" }}
                onClick={() => navigate("/statystyki")}>
                <div className="dash-module-icon"><i className="pi pi-chart-bar" /></div>
                <div className="dash-module-body">
                  <h3>Statystyki</h3>
                  <p>Statystyki floty pojazdów</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
            {hasPermission("vehicles_settings") && (
              <div className="dash-module" style={{ "--mod-color": "#64748b", "--mod-bg": "#f8fafc" }}
                onClick={() => navigate("/ustawienia-pojazdow")}>
                <div className="dash-module-icon"><i className="pi pi-cog" /></div>
                <div className="dash-module-body">
                  <h3>Ustawienia</h3>
                  <p>Konfiguracja floty</p>
                </div>
                <i className="pi pi-chevron-right dash-module-arrow" />
              </div>
            )}
          </div>
        </>
      )}
      {hasAdminAccess && (
        <>
          <h2 className="dashboard-section-title">Administracja</h2>
          <div className="dashboard-modules">
            <div className="dash-module" style={{ "--mod-color": "#8b5cf6", "--mod-bg": "#f5f3ff" }}
              onClick={() => navigate("/admin")}>
              <div className="dash-module-icon"><i className="pi pi-cog" /></div>
              <div className="dash-module-body">
                <h3>Panel Admina</h3>
                <p>Użytkownicy, firmy i słowniki</p>
              </div>
              <i className="pi pi-chevron-right dash-module-arrow" />
            </div>
          </div>
        </>
      )}

      {/* Recent activity */}
      {data && (
        <div className="dashboard-recent">
          {/* Recent docs */}
          {hasWZAccess && data.recentDocs && data.recentDocs.length > 0 && (
            <div className="dash-recent-card">
              <div className="dash-recent-header">
                <h3><i className="pi pi-file-edit" /> Ostatnie dokumenty</h3>
                <span className="dash-recent-link" onClick={() => navigate("/dokumenty")}>Zobacz wszystkie →</span>
              </div>
              <ul className="dash-recent-list">
                {data.recentDocs.map((doc) => (
                  <li key={doc.id}>
                    <div className="dash-recent-row">
                      <span className="dash-recent-primary">
                        <span className={`dash-tag ${doc.typDokumentu === "WZ" ? "dash-tag--blue" : "dash-tag--purple"}`}>{doc.typDokumentu}</span>
                        {doc.numerWZ}
                      </span>
                      <span className="dash-recent-secondary">{formatDate(doc.dataWystawienia)}</span>
                      <span className="dash-recent-amount">{formatCurrency(doc.kwotaBrutto)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recent faults */}
          {hasVehiclesAccess && data.recentFaults && data.recentFaults.length > 0 && (
            <div className="dash-recent-card">
              <div className="dash-recent-header">
                <h3><i className="pi pi-exclamation-triangle" /> Ostatnie zgłoszenia usterek</h3>
                <span className="dash-recent-link" onClick={() => navigate("/warsztat")}>Warsztat →</span>
              </div>
              <ul className="dash-recent-list">
                {data.recentFaults.map((f) => (
                  <li key={f.id}>
                    <div className="dash-recent-row">
                      <span className={`dash-tag ${faultStatusCls[f.status] || ""}`}>{faultStatusLabel[f.status]}</span>
                      <span className="dash-recent-primary" style={{ flex: 1 }}>
                        {f.is_external
                          ? <>{f.ext_vehicle_brand} {f.ext_vehicle_model} <small>({f.ext_vehicle_registration})</small> <span className="dash-tag dash-tag--purple">ZEWN.</span></>
                          : <>{f.brand} {f.model} <small>({f.registration_number})</small></>}
                      </span>
                      <span className="dash-recent-secondary">{formatDate(f.created_at)}</span>
                    </div>
                    <p className="dash-recent-desc">
                      {f.description && f.description.length > 80 ? f.description.substring(0, 80) + "…" : f.description}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="dashboard-loading">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem", color: "var(--fleet-primary, #3b82f6)" }} />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
